// "Flexible Dates" template of the Plan Calculator: a media plan over any period the user picks,
// pre-filled per period with the model's actual spend where the report has it, with its budget
// propagation where it doesn't, and with 0 elsewhere.
//
// The plan is handed to CreateSimulation as a regular template base, whose media_plan arrays are
// read by position over the period axis CreateSimulation derives from date_from / date_to. That
// axis and the date range its pickers allow are rebuilt here the same way (calculatorRange and
// planAxis), so both sides line up without changing CreateSimulation. The Plan Optimizer's template
// keeps only the plan's total, as the budget of the optimization; the Budget Simulator's keeps its
// total as the reference budget and each channel's share of it as the Frozen mix.

import moment from 'moment'
import { effectiveModelStart } from '@/utils/modelStart.js'
import { weeklyPeriodDates } from '@/utils/weeklyPeriod.js'

// The Sunday that closes the week containing date (weeks run Monday to Sunday, as in the
// Calculator's weekly axis).
function weekEnd (date) {
  const day = moment(date.slice(0, 10))
  if (day.day() !== 0) {
    day.add(7 - day.day(), 'days')
  }
  return day.format('YYYY-MM-DD')
}

function periodKey (date, frequency) {
  return frequency === 'week' ? weekEnd(date) : date.slice(0, 7)
}

// Dates the Calculator allows for the client: { start, end, all }. Mirrors the startDate, endDate
// and dates.all CreateSimulation.vue computes (brand dates, legacy weekly start, EKRM 24-month cap).
// With legacyWeeklyStart: false it mirrors CreateOptimization.vue instead, whose start is the raw
// model start; with ekrmCap: false it mirrors CreateBudgetSimulation.vue, which has no EKRM cap.
export function calculatorRange (client, options) {
  const legacyWeeklyStart = !options || options.legacyWeeklyStart !== false
  const ekrmCap = !options || options.ekrmCap !== false
  const config = client.plan_reference_model ? client.plan_reference_model.config : client.reference_model.config
  const brand = (client.type === 'mmm' || !client.custom_model) ? Object.keys(config.brands)[0] : client.custom_model.brand
  const dates = 'dates' in config.brands[brand] ? config.brands[brand].dates : config.dates
  const modelFrequency = config.saturation_granularity === 'W' ? 'week' : 'month'
  const start = legacyWeeklyStart ? effectiveModelStart(dates.start, modelFrequency, client.date_created.toDate()) : dates.start
  let end = dates.end
  if (ekrmCap && client.type === 'ekrm') {
    const maxEnd = moment(start).add(24, 'months').subtract(1, 'day').format('YYYY-MM-DD')
    end = end < maxEnd ? end : maxEnd
  }
  return { start: start, end: end, all: dates.all || [] }
}

// The periods of a plan from dateFrom to dateTo, as CreateSimulation lays out its media plan.
export function planAxis (range, dateFrom, dateTo, frequency) {
  if (frequency === 'week') {
    return weeklyPeriodDates(dateFrom, dateTo, range.end)
  }
  const from = dateFrom.slice(0, 7)
  const to = dateTo.slice(0, 7)
  return range.all.filter(date => date.slice(0, 7) >= from && date.slice(0, 7) <= to)
}

function indexSeries (target, channel, dates, values, frequency) {
  if (!Array.isArray(dates) || !Array.isArray(values)) return
  for (let i = 0; i < dates.length && i < values.length; i++) {
    const key = periodKey(dates[i], frequency)
    if (!target[key]) target[key] = {}
    target[key][channel] = (target[key][channel] || 0) + Math.round(Number(values[i]) || 0)
  }
}

// Spend per period and channel from the model report: { actual: { period: { channel: value } },
// propagation: { ... } }, periods keyed by month (YYYY-MM) or by week-closing Sunday.
export function buildPrefillLookup (report, frequency) {
  const v2 = (report && report.v2) || {}
  const lookup = { actual: {}, propagation: {} }
  const spend = frequency === 'week' ? v2.media_spend_weekly : v2.media_spend
  if (spend) {
    Object.keys(spend).forEach(channel => {
      indexSeries(lookup.actual, channel, spend[channel].x, spend[channel].y, frequency)
    })
  }
  const propagation = v2.budget_propagation
  const granularity = frequency === 'week' ? 'weekly' : 'monthly'
  if (propagation && propagation[granularity] && propagation.dates && Array.isArray(propagation.dates[granularity])) {
    Object.keys(propagation[granularity]).forEach(channel => {
      indexSeries(lookup.propagation, channel, propagation.dates[granularity], propagation[granularity][channel], frequency)
    })
  }
  return lookup
}

// The dates a Flexible Dates period can take: the part of range covered by the report's data, from
// the first period with actual spend to the last one with budget propagation (or actual spend), so
// the template never fills a stretch past the data with zeros. A week runs from its Monday to the
// Sunday that names it. Null when the data falls outside range.
export function flexibleBounds (range, lookup, frequency) {
  const keys = Object.keys(lookup.actual).concat(Object.keys(lookup.propagation)).sort()
  if (!range || keys.length === 0) return null
  const first = keys[0]
  const last = keys[keys.length - 1]
  const dataStart = frequency === 'week' ? moment(first).subtract(6, 'days').format('YYYY-MM-DD') : first + '-01'
  const dataEnd = frequency === 'week' ? last : moment(last + '-01').endOf('month').format('YYYY-MM-DD')
  const start = dataStart > range.start ? dataStart : range.start
  const end = dataEnd < range.end ? dataEnd : range.end
  return start <= end ? { start: start, end: end } : null
}

// The media plan ({ channel: [value per period] }) over axis, taking each period from the actual
// spend, else from the budget propagation, else 0. Also counts the periods each source filled and
// the total budget, for the summary shown before the plan is opened.
export function buildFlexibleMediaPlan (axis, channelNames, lookup, frequency) {
  const mediaPlan = {}
  channelNames.forEach(channel => {
    mediaPlan[channel] = []
  })
  const counts = { actual: 0, propagation: 0, empty: 0 }
  let total = 0
  axis.forEach(date => {
    const key = periodKey(date, frequency)
    let values = null
    if (lookup.actual[key]) {
      values = lookup.actual[key]
      counts.actual++
    } else if (lookup.propagation[key]) {
      values = lookup.propagation[key]
      counts.propagation++
    } else {
      counts.empty++
    }
    channelNames.forEach(channel => {
      const value = values && values[channel] ? values[channel] : 0
      mediaPlan[channel].push(value)
      total += value
    })
  })
  return { mediaPlan: mediaPlan, counts: counts, total: total }
}
